import {parseCharacterJournal} from "../utils/character-journal-payload";
import { ApiError } from '@/network/api-client';
import type {
  AdviceCommand,
  CharacterApi,
  CharacterOption,
} from '../types/character.types';
import {
  parseSnapshot,
  parseAdvice,
  parseDecision,
} from '../utils/character-payload';
import {
  characterReducer,
  initialCharacterState,
  type CharacterEvent,
} from '../utils/character-reducer';
function errorCode(error: unknown): string {
  if (error instanceof ApiError) {
    try {
      const body = JSON.parse(error.body) as { error?: string };
      return body.error ?? 'character_unavailable';
    } catch {
      return 'character_unavailable';
    }
  }
  return 'connection_failed';
}
export class CharacterController {
  private state;
private journalSequence = 0;
  private journalRequest: AbortController | null = null;
  private journalPromise: Promise<void> | null = null;
  private journalDirty = false;
  private journalAttemptedRevision = -1;
  private listeners = new Set<() => void>();
  private alive = false;
  private generation = 0;
  private requests = new Set<AbortController>();
  private snapshotRequest: AbortController | null = null;
  private busyRetries = 0;
  private timer: ReturnType<typeof setTimeout> | null = null;
  constructor(
    private readonly heroId: number | null,
    private readonly api: CharacterApi,
    private readonly newId: () => string = () => crypto.randomUUID(),
  ) {
    this.state = initialCharacterState(heroId);
  }
  getSnapshot = () => this.state;
  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };
  private dispatch(event: CharacterEvent) {
    if (!this.alive) return;
    const next = characterReducer(this.state, event);
    if (next === this.state) return;
    this.state = next;
    for (const listener of this.listeners) listener();
  }
  start = () => {
    this.alive = true;
    this.generation++;
    this.journalAttemptedRevision = -1;
    this.busyRetries = 0;
    if (this.heroId !== null) void this.refresh();
  };
  stop = () => {
    this.state = { ...this.state, sending: false, journalLoading:false };
    this.alive = false;
    this.generation++;
    for (const request of this.requests) request.abort();
    this.requests.clear();
    this.snapshotRequest = null;
    this.busyRetries = 0;
this.journalSequence++;this.journalRequest=null;this.journalPromise=null;this.journalDirty=false;
    if (this.timer !== null) clearTimeout(this.timer);
    this.timer = null;
  };
  applySnapshot = (payload: unknown) => {
    const value = parseSnapshot(payload);
    if (value) {
      this.dispatch({ type: 'snapshot', value });
      const revision = this.state.snapshot?.revision;
      if (revision !== undefined && revision > this.journalAttemptedRevision) {
        if (this.journalRequest) this.journalDirty = true;
        else void this.loadJournal();
      }
    }
  };
  applyAdvice = (payload: unknown) => {
    const value = parseAdvice(payload);
    if (value) this.dispatch({ type: 'advice', value });
  };
  applyDecision = (payload: unknown) => {
    const value = parseDecision(payload);
    if (value) {
      const previous = this.state.lastDecisionRevision;
      this.dispatch({ type: 'decision', value });
      if (this.state.lastDecisionRevision > previous) {
        if (this.journalRequest) this.journalDirty = true;
        else void this.loadJournal();
      }
    }
  };
  refresh = async () => {
    if (!this.alive || this.heroId === null || this.snapshotRequest) return;
    if (this.timer !== null) clearTimeout(this.timer);
    this.timer = null;
    const generation = this.generation;
    const request = new AbortController();
    this.snapshotRequest = request;
    let retryDelay: number | null = null;
    this.requests.add(request);
    try {
      const value = await this.api.snapshot(request.signal);
      if (!request.signal.aborted && generation === this.generation)
        {
        this.busyRetries = 0;
        this.applySnapshot(value);

      }
    } catch (error) {
      if (!request.signal.aborted && generation === this.generation) {
        const code = errorCode(error);
        if (code === 'hero_busy' || code === 'character_busy') {
          const delays = [500, 1000, 2000];
          retryDelay = delays[this.busyRetries++] ?? null;
          // A read conflict is storage/engine ownership contention, not a
          // character action. Retain the last profile and command errors.
          if (!this.state.error || ['character_refreshing', 'character_unavailable'].includes(this.state.error))
            this.dispatch({ type: 'snapshot_error', value: retryDelay === null ? 'character_unavailable' : 'character_refreshing' });
          if (retryDelay === null) this.busyRetries = 0;
        } else { this.busyRetries = 0; this.failure(error); }
      }
    } finally {
      this.requests.delete(request);
      if (this.snapshotRequest === request) this.snapshotRequest = null;
      if (
        this.alive &&
        generation === this.generation &&
        !this.state.unavailable
      ) {
        if (this.timer !== null) clearTimeout(this.timer);
        this.timer = setTimeout(
          () => void this.refresh(),
          retryDelay ?? (this.state.snapshot?.active?.status === 'active' ? 2000 : 15000),
        );
      }
    }
  };
  // One journal request per bootstrap/committed change/open; no independent polling timer.
  loadJournal = (): Promise<void> => {
    if (!this.alive || this.heroId === null) return Promise.resolve();
    if (this.journalPromise) return this.journalPromise;
    const generation = this.generation;
    this.journalPromise = this.readJournal(generation);
    return this.journalPromise;
  };
  private async readJournal(generation: number): Promise<void> {
    const request = new AbortController();
    this.journalRequest = request;
    this.journalDirty = false;
    this.journalAttemptedRevision = this.state.snapshot?.revision ?? 0;
    this.requests.add(request);
    const sequence = ++this.journalSequence;
    this.dispatch({ type: 'journal_begin' });
    try {
      const raw = await this.api.journal(request.signal);
      if (request.signal.aborted || generation !== this.generation || sequence !== this.journalSequence) return;
      const value = parseCharacterJournal(raw);
      if (!value || value.heroId !== this.heroId) this.dispatch({ type: 'journal_error' });
      else this.dispatch({ type: 'journal', value });
    } catch {
      if (!request.signal.aborted && generation === this.generation && sequence === this.journalSequence) this.dispatch({ type: 'journal_error' });
    } finally {
      this.requests.delete(request);
      if (this.journalRequest === request) {
        this.journalRequest = null;
        this.journalPromise = null;
        if (this.alive && generation === this.generation && this.journalDirty) void this.loadJournal();
      }
    }
  }
  dismissDecision = () => this.dispatch({ type: 'dismiss' });
  private failure(error: unknown, commandFailure = false) {
    const code = errorCode(error);
    const terminal = commandFailure && [
      'invalid_advice',
      'advice_too_late',
      'advice_already_recorded',
      'advice_binding_conflict',
      'encounter_changed',
      'encounter_completed',
      'encounter_not_found',
    ].includes(code);
    this.dispatch(
      code === 'character_disabled'
        ? { type: 'unavailable' }
        : terminal
          ? { type: 'terminal', value: code }
          : { type: commandFailure ? 'error' : 'snapshot_error', value: code },
    );
    if (terminal) void this.refresh();
  }
  advise = (option: CharacterOption) => {
    const active = this.state.snapshot?.active;
    if (
      !this.alive ||
      this.state.pending ||
      !active ||
      active.status !== 'active' ||
      active.revision < this.state.lastDecisionRevision ||
      !option.available ||
      active.advice
    )
      return;
    const registered = active.options.find(
      (value) => value.id === option.id && value.available,
    );
    if (!registered) return;
    const command: AdviceCommand = {
      commandId: this.newId(),
      instanceId: active.id,
      stageRevision: active.revision,
      suggestedActionId: registered.id,
      targetIds: [...registered.targetIds],
    };
    this.dispatch({ type: 'begin', value: command });
    void this.send(command);
  };
  retry = () => {
    if (!this.alive || !this.state.pending || this.state.sending) return;
    this.dispatch({ type: 'retry' });
    void this.send(this.state.pending);
  };
  private async send(command: AdviceCommand) {
    const generation = this.generation;
    const request = new AbortController();
    this.requests.add(request);
    try {
      const value = await this.api.advise(command, request.signal);
      if (request.signal.aborted || generation !== this.generation) return;
      this.applyAdvice(value.advice);
      if (value.decision) this.applyDecision(value.decision);
      void this.refresh();
    } catch (error) {
      if (
        !request.signal.aborted &&
        generation === this.generation &&
        this.state.pending?.commandId === command.commandId &&
        this.state.pending.instanceId === command.instanceId
      )
        this.failure(error, true);
    } finally {
      this.requests.delete(request);
    }
  }
}
